import React from 'react';

const ROLE_CONFIGS = {
  board: {
    badge: 'Board Member',
    greeting: (first) => `Hi ${first}! 👋`,
    subtext: "I'm genuinely excited to finally put this in your hands. Here's what to explore first.",
    features: [
      { gradient: 'linear-gradient(135deg,#1a4d0f 0%,#2D5016 60%,#3a6a1e 100%)',
        title: 'AI Search',
        desc: "Ask anything about our CC&Rs, bylaws, or rules. Real answers, with citations, in seconds.",
        cta: 'Try it now', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#0b5e47 0%,#0d7a5e 60%,#0f9070 100%)',
        title: 'Inbox + ACC',
        desc: "Homeowner messages and architectural requests come straight here. You're already in it.",
        cta: "You're in it", tabId: 'messages' },
      { gradient: 'linear-gradient(135deg,#1a3a6b 0%,#1e4d8c 60%,#2460a8 100%)',
        title: 'Document Library',
        desc: 'All 9 governing documents — uploaded, indexed, and AI-searchable. No more hunting for PDFs.',
        cta: 'Browse library', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#5a3800 0%,#7a4e00 60%,#9a6600 100%)',
        title: 'Directory',
        desc: 'Your neighbors, board members, and management — all in one place with full contact info.',
        cta: 'Meet everyone', tabId: 'members' },
    ],
    signature: "Proud creator of this portal — with a little help from Claude Code 😄",
  },
  management: {
    badge: 'Property Management',
    greeting: (first) => `Hi ${first}! 👋`,
    subtext: "So glad you're here — this portal was built with your day-to-day in mind. Let me show you around.",
    features: [
      { gradient: 'linear-gradient(135deg,#1a4d0f 0%,#2D5016 60%,#3a6a1e 100%)',
        title: 'AI Search',
        desc: "Homeowners can now answer their own questions about community rules — which means fewer calls to you.",
        cta: 'See it in action', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#0b5e47 0%,#0d7a5e 60%,#0f9070 100%)',
        title: 'Inbox + ACC',
        desc: 'All homeowner messages and ACC applications land here. Respond directly, no email chains.',
        cta: "You're in it", tabId: 'messages' },
      { gradient: 'linear-gradient(135deg,#1a3a6b 0%,#1e4d8c 60%,#2460a8 100%)',
        title: 'Document Library',
        desc: 'Every governing document, uploaded and indexed. No more digging through filing cabinets.',
        cta: 'Browse library', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#5a3800 0%,#7a4e00 60%,#9a6600 100%)',
        title: 'Directory',
        desc: 'All residents and their contact info, organized by role. Always up to date.',
        cta: 'Open directory', tabId: 'members' },
    ],
    signature: "Portal builder, board treasurer, occasional AI whisperer 😄",
  },
  member: {
    badge: 'Homeowner',
    greeting: (first) => `Hi ${first}! 👋`,
    subtext: "Welcome to your community portal — I'm so excited to share this with you. Here's what makes it worth exploring.",
    features: [
      { gradient: 'linear-gradient(135deg,#1a4d0f 0%,#2D5016 60%,#3a6a1e 100%)',
        title: 'AI Search',
        desc: "Have a question about community rules? Just ask — in plain English. You'll get a real answer with citations.",
        cta: 'Ask anything', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#0b5e47 0%,#0d7a5e 60%,#0f9070 100%)',
        title: 'Inbox + ACC',
        desc: 'Your direct line to the board. Send a message, submit an ACC request — all from right here.',
        cta: "You're in it", tabId: 'messages' },
      { gradient: 'linear-gradient(135deg,#1a3a6b 0%,#1e4d8c 60%,#2460a8 100%)',
        title: 'Document Library',
        desc: "CC&Rs, bylaws, amendments — all your governing documents in one place. Yours to read anytime.",
        cta: 'Browse docs', tabId: 'documents' },
      { gradient: 'linear-gradient(135deg,#5a3800 0%,#7a4e00 60%,#9a6600 100%)',
        title: 'Directory',
        desc: "Meet your neighbors, find board contact info, and see who's who in the community.",
        cta: 'Meet neighbors', tabId: 'directory' },
    ],
    signature: "Built this for our community, with love and way too much coffee ☕",
  },
};

// Decorative background patterns per tile (index 0-3)
const TILE_PATTERNS = [
  '<circle cx="40" cy="40" r="38" stroke="white" stroke-width="2" fill="none"/><circle cx="40" cy="40" r="25" stroke="white" stroke-width="1.5" fill="none"/>',
  '<rect x="5" y="5" width="70" height="70" rx="12" stroke="white" stroke-width="2" fill="none"/><rect x="15" y="15" width="50" height="50" rx="8" stroke="white" stroke-width="1.5" fill="none"/>',
  '<path d="M10 70 L10 10 L55 10 L70 25 L70 70 Z" stroke="white" stroke-width="2" fill="none"/><path d="M55 10 L55 25 L70 25" stroke="white" stroke-width="1.5" fill="none"/>',
  '<circle cx="30" cy="30" r="18" stroke="white" stroke-width="2" fill="none"/><circle cx="55" cy="25" r="14" stroke="white" stroke-width="1.5" fill="none"/>',
];

const TILE_ICONS = [
  '<circle cx="10" cy="10" r="8" stroke="white" stroke-width="1.6"/><path d="M7 10h6M10 7v6" stroke="white" stroke-width="1.6" stroke-linecap="round"/>',
  '<path d="M17 13c0 1.1-.9 2-2 2H5l-2 2V5c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v8z" stroke="white" stroke-width="1.6" stroke-linejoin="round"/>',
  '<path d="M4 18V4l10 0 4 4v10H4z" stroke="white" stroke-width="1.6" stroke-linejoin="round"/><path d="M14 4v4h4" stroke="white" stroke-width="1.6"/><path d="M7 9h6M7 12h4" stroke="white" stroke-width="1.4" stroke-linecap="round"/>',
  '<circle cx="7" cy="6" r="3" stroke="white" stroke-width="1.6"/><circle cx="13" cy="6" r="3" stroke="white" stroke-width="1.6"/><path d="M2 17c0-3 2.2-5 5-5" stroke="white" stroke-width="1.6" stroke-linecap="round"/><path d="M11 12c2.8 0 7 2 7 5" stroke="white" stroke-width="1.6" stroke-linecap="round"/>',
];

export default function WelcomeMessageCard({
  recipientName,
  recipientRole,
  personalNote,
  senderName = 'HOA Team',
  senderTitle = 'Portal Administrator',
  hoaName = '',
}) {
  const roleKey = recipientRole === 'management' ? 'management'
    : recipientRole === 'board' || recipientRole === 'admin' ? 'board'
    : 'member';
  const config = ROLE_CONFIGS[roleKey];
  const firstName = recipientName ? recipientName.split(' ')[0] : 'there';

  const senderInitials = senderName
    .split(/s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'HOA';
  const resolvedSenderTitle =
    senderTitle || (hoaName ? `Portal Administrator, ${hoaName}` : 'Portal Administrator');

  return (
    <div style={{
      borderRadius: 16, overflow: 'hidden',
      border: '1.5px solid #c8e2a0', }}>
      {/* ── HERO ── */}
      <div style={{
        background: '#2D5016', padding: '32px 28px 26px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', top: -40, right: -40,
          width: 200, height: 200, borderRadius: '50%',
          background: 'rgba(126,200,80,.15)',
        }} />
        {/* Tree silhouettes */}
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: .1 }}
          viewBox="0 0 700 80" preserveAspectRatio="none" width="100%" height="60">
          <polygon points="0,80 30,20 60,80" fill="white"/>
          <polygon points="40,80 80,5 120,80" fill="white"/>
          <polygon points="100,80 130,30 160,80" fill="white"/>
          <polygon points="155,80 190,10 225,80" fill="white"/>
          <polygon points="210,80 240,35 270,80" fill="white"/>
          <polygon points="270,80 310,15 350,80" fill="white"/>
          <polygon points="340,80 370,28 400,80" fill="white"/>
          <polygon points="400,80 440,8 480,80" fill="white"/>
          <polygon points="460,80 490,32 520,80" fill="white"/>
          <polygon points="510,80 555,12 600,80" fill="white"/>
          <polygon points="580,80 620,25 660,80" fill="white"/>
          <polygon points="640,80 680,18 720,80" fill="white"/>
        </svg>
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.22)',
            borderRadius: 20, padding: '3px 11px', fontSize: 10.5,
            color: 'rgba(255,255,255,.88)', fontWeight: 600, marginBottom: 14,
            letterSpacing: '.04em',
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#8fd44a' }} />
            {config.badge}
          </div>
          <h2 style={{
            fontSize: 28, fontWeight: 700, color: '#fff',
            lineHeight: 1.2, margin: '0 0 10px', letterSpacing: '-.2px',
            }}>
            {config.greeting(firstName)}
          </h2>
          <p style={{
            fontSize: 14, color: 'rgba(255,255,255,.82)',
            lineHeight: 1.65, margin: 0, maxWidth: 480,
            }}>
            {config.subtext}
          </p>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{
        padding: '22px 24px',
        background: 'linear-gradient(160deg,#f5faee 0%,#eef6e2 100%)',
      }}>
        {/* Personal note */}
        <p style={{
          fontSize: 14.5, color: '#3b4e28', lineHeight: 1.75,
          marginBottom: 22, paddingBottom: 20,
          borderBottom: '1.5px dashed #c8e2a0',
          }}>
          {personalNote}
        </p>

        {/* Section label */}
        <div style={{
          fontSize: 10.5, fontWeight: 700, color: '#6b9040',
          letterSpacing: '.09em', textTransform: 'uppercase',
          marginBottom: 13, display: 'flex', alignItems: 'center', gap: 8,
          }}>
          Start here
          <div style={{ flex: 1, height: 1, background: '#c8e2a0' }} />
        </div>

        {/* Feature grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 12, marginBottom: 22,
        }}>
          {config.features.map((f, i) => (
            <a href={`?tab=${f.tabId}`} key={i} style={{
              background: f.gradient, borderRadius: 13,
              padding: 16, display: 'flex', flexDirection: 'column',
              position: 'relative', overflow: 'hidden', textDecoration: 'none',
              transition: 'transform 0.2s', cursor: 'pointer'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
              {/* Decorative pattern */}
              <svg style={{ position: 'absolute', right: -10, bottom: -10, opacity: .08 }}
                width="80" height="80" viewBox="0 0 80 80"
                dangerouslySetInnerHTML={{ __html: TILE_PATTERNS[i] }} />
              {/* Icon */}
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,.18)',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', marginBottom: 12, flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"
                  dangerouslySetInnerHTML={{ __html: TILE_ICONS[i] }} />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#fff', marginBottom: 5, }}>
                {f.title}
              </div>
              <div style={{
                fontSize: 12, color: 'rgba(255,255,255,.80)',
                lineHeight: 1.55, marginBottom: 10, flex: 1, }}>
                {f.desc}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.95)',
                background: 'rgba(255,255,255,.15)',
                border: '1px solid rgba(255,255,255,.25)',
                borderRadius: 20, padding: '4px 10px', width: 'fit-content',
                }}>
                {f.cta} →
              </div>
            </a>
          ))}
        </div>

        {/* Signature */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          paddingTop: 18, borderTop: '1.5px dashed #c8e2a0',
        }}>
          <div style={{
            width: 46, height: 46, borderRadius: '50%',
            background: '#2D5016', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 15, fontWeight: 800,
            color: '#fff', flexShrink: 0,
          }}>
            {senderInitials}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#2D5016', marginBottom: 2, }}>
              {senderName}
            </div>
            <div style={{ fontSize: 12, color: '#6b9040', fontStyle: 'italic', lineHeight: 1.5, }}>
              {resolvedSenderTitle}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
